import { compose } from 'redux'
import { UseQueryResult } from '@tanstack/react-query'
import { track } from 'lib/analytics'
import Link from 'components/link'
import { useEffect, useState } from 'react'
import { parseFilterQueryParams } from 'store/lib'
import Image from 'components/image'
import { InjectedRouter } from 'react-router'
import {
  withActiveSortParams,
  withActiveProductFilters,
} from '../../../../reducers'
import ProductGrid, { mapHeroesToProductCards } from '../../../product-grid'
import { useBrandProducts } from '../../../../api/hooks'
import { filterEmptyFilters } from '../../../product-filters/helpers'
import FacetsBar, {
  SelectedFilters,
} from '../../../Filters/components/FacetsBar/FacetsBar'

interface HeroProductsBaseProps {
  brandId: number
  location: Location
  sortParams?: {
    sort?: string
    order?: string
  }
  productFilters?: {
    available?: boolean
  }
  brandName: string
  guides: Post[]
  router: InjectedRouter
}

const HeroProductsBase = ({
  brandId,
  location,
  sortParams,
  productFilters,
  brandName,
  guides,
  router,
}: HeroProductsBaseProps) => {
  const deprecatedFilters = filterEmptyFilters(productFilters)
  const hasDeprecatedFilters = Object.keys(deprecatedFilters).length > 0
  const displayNewFacetsAndFilters = !hasDeprecatedFilters
  const [selectedFilters, setSelectedFilters] = useState(() => {
    const initialFilters = location.search
      ? parseFilterQueryParams(location.search)
      : {}
    return initialFilters as SelectedFilters
  })
  const [filtersToApply, setFiltersToApply] = useState(() =>
    displayNewFacetsAndFilters
      ? parseFilterQueryParams(location.search)
      : deprecatedFilters
  )
  const sortOption = new URLSearchParams(location.search).get('sort')

  useEffect(() => {
    const newFilters = displayNewFacetsAndFilters
      ? parseFilterQueryParams(location.search)
      : deprecatedFilters
    setFiltersToApply(newFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, productFilters])

  const handleClearFilters = () => {
    setFiltersToApply({})
    router.push(location.pathname)
  }

  const { data, isLoading } = useBrandProducts(brandId, {
    filters: filtersToApply,
    inFacetsFeature: displayNewFacetsAndFilters,
    sort: sortOption,
    orderBy: {
      key: sortParams?.sort,
      direction: sortParams?.order,
    },
  }) as UseQueryResult<APIResponse.Data>

  const productCards = isLoading
    ? null
    : data?.heroProducts.map(mapHeroesToProductCards)

  const facets = isLoading ? null : data?.facets

  if (!isLoading && data && data.heroProducts.length > 0) {
    track.storeBrandViewed({
      eventLocation: track.EventLocation.STORE_MINUS_BRAND,
      brand: brandName,
    })
  }

  return (
    <>
      {displayNewFacetsAndFilters && facets && (
        <>
          <FacetsBar
            facets={facets}
            facetsType="brand"
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            slug={brandId.toString()}
          />
        </>
      )}
      {productCards?.length === 0 && (
        <div className="well flex col paxl align-center">
          <div className="text-center text-muted pvxxl">
            <div
              className="mbm flex col align-center"
              style={{ maxWidth: 480 }}
            >
              <Image
                src="//images.babylist.com/image/upload/v1715118923/spilled_milk_asfyrb.svg"
                width={185}
              />
              <div className="mtl">
                Whoops! We couldn't find any matches that fit your current
                filters. Adjust your selections or{' '}
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link
                  className="text-underline link-info"
                  onClick={handleClearFilters}
                >
                  clear all filters
                </Link>{' '}
                to see all available products.
              </div>
            </div>
          </div>
        </div>
      )}
      <ProductGrid
        activeSlugSet={null}
        className="mtl"
        guides={guides}
        productCards={productCards}
      />
    </>
  )
}

export default compose<React.ComponentType<HeroProductsBaseProps>>(
  withActiveProductFilters(),
  withActiveSortParams()
)(HeroProductsBase)
